import React, { useState, useMemo } from 'react';
import Card from '../../../../shared/components/Card';
import usePageTranslation from '../../../../shared/hooks/usePageTranslation';

export type LogActionType =
  | 'member_joined'
  | 'member_left'
  | 'candidate_accepted'
  | 'candidate_rejected'
  | 'roles_updated'
  | 'bank_expense'
  | 'contribution_updated'
  | 'settings_updated'
  | 'crm_created'
  | 'post_created';

export interface AllianceLogEntry {
  id: string;
  type: LogActionType;
  date: string;
  actor: string;
  target?: string;
  details?: string;
}

const MOCK_LOGS: AllianceLogEntry[] = [
  { id: '1', type: 'member_joined', date: '2026-02-23T14:32:00', actor: 'paulf31' },
  { id: '2', type: 'candidate_accepted', date: '2026-02-23T13:15:00', actor: 'Wiglaum', target: 'Matteospv' },
  { id: '3', type: 'roles_updated', date: '2026-02-23T11:08:00', actor: 'M0rgan', target: 'Fr3d', details: 'Formateur de l\'alliance, Personnel' },
  { id: '4', type: 'bank_expense', date: '2026-02-22T17:12:00', actor: 'M0rgan', details: 'CH Alliance 54 Piedmont 1 - Garde prolongée · 200 000 €' },
  { id: '5', type: 'contribution_updated', date: '2026-02-22T16:45:00', actor: 'Victor77', details: '5 %' },
  { id: '6', type: 'post_created', date: '2026-02-22T10:20:00', actor: 'kiliannb' },
  { id: '7', type: 'candidate_rejected', date: '2026-02-21T18:00:00', actor: 'Hugues53', target: 'UserX' },
  { id: '8', type: 'settings_updated', date: '2026-02-21T09:30:00', actor: 'Wiglaum' },
  { id: '9', type: 'crm_created', date: '2026-02-20T14:00:00', actor: 'M0rgan', details: 'Centre Piedmont 1' },
  { id: '10', type: 'member_left', date: '2026-02-19T12:00:00', actor: 'AncienMembre' },
];

const ACTION_TYPES: LogActionType[] = [
  'member_joined',
  'member_left',
  'candidate_accepted',
  'candidate_rejected',
  'roles_updated',
  'bank_expense',
  'contribution_updated',
  'settings_updated',
  'crm_created',
  'post_created',
];

const AllianceLogs: React.FC = () => {
  const { t } = usePageTranslation('game', 'AlliancePage');
  const [filterType, setFilterType] = useState<LogActionType | 'all'>('all');

  const filteredLogs = useMemo(() => {
    const list = [...MOCK_LOGS].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    if (filterType === 'all') return list;
    return list.filter((log) => log.type === filterType);
  }, [filterType]);

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const dateStr = d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: d.getFullYear() !== today.getFullYear() ? 'numeric' : undefined });
    const timeStr = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    if (d.toDateString() === today.toDateString()) return `Aujourd'hui ${timeStr}`;
    if (d.toDateString() === yesterday.toDateString()) return `Hier ${timeStr}`;
    return `${dateStr} ${timeStr}`;
  };

  const getActionLabel = (entry: AllianceLogEntry): string => {
    const key = `logs.action_${entry.type}` as keyof typeof t;
    const label = t(key as string);
    if (entry.target) return `${label} ${entry.target}`;
    return label;
  };

  const getActionIcon = (type: LogActionType) => {
    const base = 'w-5 h-5 flex-shrink-0';
    switch (type) {
      case 'member_joined':
        return <svg className={`${base} text-green-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>;
      case 'member_left':
        return <svg className={`${base} text-orange-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>;
      case 'candidate_accepted':
        return <svg className={`${base} text-green-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
      case 'candidate_rejected':
        return <svg className={`${base} text-red-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
      case 'roles_updated':
        return <svg className={`${base} text-indigo-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>;
      case 'bank_expense':
        return <svg className={`${base} text-amber-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
      case 'contribution_updated':
        return <svg className={`${base} text-cyan-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" /></svg>;
      case 'settings_updated':
        return <svg className={`${base} text-gray-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>;
      case 'crm_created':
        return <svg className={`${base} text-blue-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>;
      case 'post_created':
        return <svg className={`${base} text-purple-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" /></svg>;
      default:
        return <svg className={`${base} text-gray-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
    }
  };

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white">{t('logs.title')}</h2>
        <p className="text-sm text-gray-500 mt-1">{t('logs.subtitle')}</p>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <label className="flex items-center gap-2 text-sm text-gray-400">
          <span>{t('logs.colAction')}</span>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as LogActionType | 'all')}
            className="rounded-lg bg-gray-800 border border-gray-600 text-white px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="all">{t('logs.filterAll')}</option>
            {ACTION_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`logs.action_${type}` as string)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="rounded-lg border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-800/80 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider w-40">{t('logs.colDate')}</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider w-12" aria-hidden />
                <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider w-36">{t('logs.colMember')}</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wider">{t('logs.colDetails')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                    {t('logs.noResults')}
                  </td>
                </tr>
              ) : (
                filteredLogs.map((entry) => (
                  <tr key={entry.id} className="border-t border-gray-700/50 hover:bg-gray-800/30">
                    <td className="px-4 py-3 text-sm text-gray-400 whitespace-nowrap tabular-nums">
                      {formatDate(entry.date)}
                    </td>
                    <td className="px-4 py-3">
                      {getActionIcon(entry.type)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-medium text-white">{entry.actor}</span>
                      <span className="text-gray-400"> — {getActionLabel(entry)}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 max-w-md">
                      {entry.details || '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
};

export default AllianceLogs;
